import React, { useState, useMemo } from "react"
import {
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  CardHeader,
  CardFooter,
  Alert
} from "reactstrap"
import Select from "react-select"
import Datetime from "react-datetime"
import { withRouter } from "react-router"
import { flowRight as compose } from "lodash"
import { useMutation, useQuery } from "@apollo/react-hooks"
import moment from "moment"
import { BackButton } from "components/utility"
import { BarLoader, Spinner } from "components/spinner"
import { getCombinedOption, getOptions, getAssociatedOptions } from "utils"
import {
  CREATE_MAINTAIN_HISTORY,
  UPDATE_MAINTAIN_HISTORY,
  GET_COMPANIES,
  GET_CATEGORIES,
  GET_SUBCATEGORIES
} from "../gql"

const MaintainHistoryForm = props => {
  const [localData, setLocalData] = useState(
    props.data || {
      content: "",
      type: "",
      maintenanceTime: moment().format("YYYY-MM-DD")
    }
  )

  const [
    updateObject,
    { loading: updateLoading, error: updateError }
  ] = useMutation(UPDATE_MAINTAIN_HISTORY)

  const [
    createObject,
    { loading: createLoading, error: createError }
  ] = useMutation(CREATE_MAINTAIN_HISTORY)

  const { data: companyData, loading: loadingCompanyData } = useQuery(
    GET_COMPANIES,
    {
      fetchPolicy: "cache-and-network"
    }
  )

  const { data: categoryData, loading: loadingCategoryData } = useQuery(
    GET_CATEGORIES,
    {
      fetchPolicy: "cache-and-network"
    }
  )

  const { data: subcategoryData, loading: loadingSubcategoryData } = useQuery(
    GET_SUBCATEGORIES,
    {
      fetchPolicy: "cache-and-network"
    }
  )

  const [selectedCompany, setSelectedCompany] = useState(
    (localData?.companyDevice?.company && {
      value: localData.companyDevice.company.id,
      label: localData.companyDevice.company.name
    }) ||
      undefined
  )

  const [selectedCategory, setSelectedCategory] = useState(
    (localData?.product && {
      value: localData?.product?.category?.id || undefined,
      label: localData?.product?.category?.name || undefined
    }) ||
      undefined
  )

  const [selectedSubcategory, setSelectedSubcategory] = useState(
    (localData?.product && {
      value: localData?.product?.subcategory?.id || undefined,
      label: localData?.product?.subcategory?.name || undefined
    }) ||
      undefined
  )
  const [selectedProduct, setSelectedProduct] = useState(
    (localData?.product && {
      value: localData?.product?.id || undefined,
      label: localData?.product?.name || undefined
    }) ||
      undefined
  )

  const onFieldChange = (key, value) => {
    setLocalData({
      ...localData,
      [key]: value
    })
  }

  const checkRequire = () => {
    return (
      selectedCompanyDevice?.value === undefined ||
      selectedProduct?.value === undefined
    )
  }

  const variableFromData = () => {
    return {
      ...(localData.id && { id: localData.id }),
      input: {
        content: localData.content,
        companyDevice: selectedCompanyDevice.value,
        product: selectedProduct.value,
        maintenanceTime: localData.maintenanceTime
      }
    }
  }

  const onSaveButtonClick = async () => {
    if (localData.id) {
      await updateObject({
        variables: variableFromData()
      })
    } else {
      await createObject({
        variables: variableFromData()
      })
    }
    props.history.goBack()
  }

  const [selectedCompanyDevice, setSelectedCompanyDevice] = useState(
    localData?.companyDevice?.device &&
      getCombinedOption(localData.companyDevice, ["name", "number"], "device")
  )

  const companyOptions = useMemo(
    () => getOptions(companyData?.companies?.data, ["name"]),
    [companyData]
  )

  const companyDeviceOptions = useMemo(
    () =>
      getAssociatedOptions(
        companyData?.companies?.data,
        selectedCompany,
        "companyDevices",
        ["name", "number"],
        "device"
      ),
    [companyData, selectedCompany]
  )

  const categoryOptions = useMemo(() => {
    const result = categoryData?.categories?.data?.map(category => {
      const categoryName = category.name + (!category.isEnable && " (已停用)")
      return { value: category.id, label: categoryName }
    })
    return result
  }, [categoryData])

  const subcategoryOptions = useMemo(() => {
    return (
      selectedCategory &&
      categoryData?.categories?.data
        .filter(category => category.id === selectedCategory.value)
        .map(item => {
          return item.subcategories.map(subcategory => {
            const categoryName =
              subcategory.name + (!subcategory.isEnable && " (已停用)")
            return { value: subcategory.id, label: categoryName }
          })
        })[0]
    )
  }, [selectedCategory, categoryData])

  const productOptions = useMemo(
    () =>
      getAssociatedOptions(
        subcategoryData?.subcategories?.data,
        selectedSubcategory,
        "products",
        ["name"]
      ),
    [subcategoryData, selectedSubcategory]
  )

  return (
    <>
      <Card>
        <CardHeader>
          <BackButton history={props.history} left />
        </CardHeader>

        <CardBody>
          {loadingCompanyData ||
          loadingCategoryData ||
          loadingSubcategoryData ||
          !companyData ||
          !categoryData ||
          !subcategoryData ? (
            <Spinner />
          ) : (
            <Form
              style={{
                marginLeft: "10px",
                marginRight: "10px"
              }}
            >
              <FormGroup row>
                <Label>選擇公司 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇公司"
                    value={selectedCompany}
                    options={companyOptions}
                    onChange={option => {
                      setSelectedCompany(option)
                      setSelectedCompanyDevice("")
                    }}
                  />
                </div>
              </FormGroup>
              <FormGroup row>
                <Label>選擇公司設備 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇公司設備"
                    value={selectedCompanyDevice}
                    options={companyDeviceOptions}
                    onChange={option => setSelectedCompanyDevice(option)}
                  />
                </div>
              </FormGroup>
              <FormGroup row>
                <Label>選擇大分類 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇大分類"
                    value={selectedCategory}
                    options={categoryOptions}
                    onChange={option => {
                      setSelectedCategory(option)
                      setSelectedSubcategory("")
                      setSelectedProduct("")
                    }}
                  />
                </div>
              </FormGroup>
              <FormGroup row>
                <Label>選擇小分類 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇小分類"
                    value={selectedSubcategory}
                    options={subcategoryOptions}
                    onChange={option => {
                      setSelectedSubcategory(option)
                      setSelectedProduct("")
                    }}
                  />
                </div>
              </FormGroup>
              <FormGroup row>
                <Label>選擇耗材 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇耗材"
                    value={selectedProduct}
                    options={productOptions}
                    onChange={option => setSelectedProduct(option)}
                  />
                </div>
              </FormGroup>
              <FormGroup row style={{ marginBottom: "0" }}>
                <Label for="startTime">維護日期</Label>
              </FormGroup>
              <div style={{ marginBottom: "10px", marginLeft: "-12px" }}>
                <Datetime
                  dateFormat="YYYY-MM-DD"
                  timeFormat={false}
                  closeOnSelect={true}
                  closeOnTab={true}
                  value={moment(localData.maintenanceTime).format("YYYY-MM-DD")}
                  onChange={date => {
                    onFieldChange("maintenanceTime", date.format("YYYY-MM-DD"))
                  }}
                  inputProps={{ placeholder: "選擇維護時間", readOnly: true }}
                />
              </div>
              <FormGroup row>
                <Label for="content">維護內容</Label>
                <Input
                  type="textarea"
                  name="content"
                  id="content"
                  placeholder="描述"
                  value={localData.content}
                  onChange={e => {
                    onFieldChange("content", e.target.value)
                  }}
                />
              </FormGroup>
            </Form>
          )}
        </CardBody>
        <CardFooter>
          <div>
            <Button
              color="primary"
              disabled={checkRequire()}
              onClick={onSaveButtonClick}
            >
              送出
            </Button>
          </div>
        </CardFooter>
        <BarLoader loading={updateLoading || createLoading} />
      </Card>
      {(createError || updateError) && (
        <Alert color="danger">
          錯誤訊息：{`${createError || updateError}`}
        </Alert>
      )}
    </>
  )
}

export default compose(withRouter)(MaintainHistoryForm)
